// src/manager/ManagerLogin.tsx — Login ID based (MGR######)
import { useState, useEffect } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { useManager } from './hooks/useManager';
import ErrorDisplay from '../shared/components/ErrorDisplay';

interface FormData { login_id: string; password: string; }

export default function ManagerLogin() {
  const { register, handleSubmit, formState: { errors } } = useForm<FormData>();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { setError, error, fetchCsrfToken, disconnectManagerSocket, clearManagerSession } = useManager();
  const navigate = useNavigate();

  useEffect(() => { fetchCsrfToken().catch(() => {}); }, [fetchCsrfToken]);

  const onSubmit = async (data: FormData) => {
    setLoading(true); setError('');
    try {
      disconnectManagerSocket();
      clearManagerSession();
      const csrfToken = await fetchCsrfToken();
      const res = await fetch('/manager/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        credentials: 'include',
        body: JSON.stringify({ login_id: data.login_id.trim(), password: data.password }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error?.message || 'Login failed');
      localStorage.setItem('managerToken', result.token);
      if (result.manager) {
        localStorage.setItem('managerInfo', JSON.stringify(result.manager));
        if (result.manager.id) {
          localStorage.setItem('managerId', result.manager.id);
        }
      }
      navigate('/manager');
    } catch (err: any) {
      setError(err.message || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-br from-indigo-900 via-purple-800 to-pink-700 text-white">
      <header className="flex justify-between items-center p-4 sm:p-6 bg-black bg-opacity-60 shadow-lg">
        <div className="flex items-center gap-3">
          <img src="/BolsaFish.png" alt="Bolsa Fish" className="bf-logo-ring h-10 w-10 sm:h-12 sm:w-12 rounded-full object-contain p-1" />
          <h1 className="text-2xl sm:text-3xl font-extrabold">Bolsa Fish</h1>
        </div>
        <Link to="/admin/login" className="bf-glossy-btn px-4 py-2 rounded-xl transition">Admin Login</Link>
      </header>

      <main className="flex-grow flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-md text-gray-800">
          <h1 className="text-2xl sm:text-3xl font-bold text-center mb-2">Manager Login</h1>
          <p className="text-center text-gray-500 text-sm mb-6">Use your Manager Login ID (MGR######)</p>

          {error && <ErrorDisplay error={error} onDismiss={() => setError('')} />}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Manager Login ID</label>
              <input placeholder="e.g. MGR123456"
                {...register('login_id', { required: 'Login ID required' })}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-600 font-mono tracking-wider" />
              {errors.login_id && <p className="text-red-600 text-sm">{errors.login_id.message}</p>}
            </div>

            <div className="relative">
              <label className="block text-sm font-medium mb-1">Password</label>
              <input type={showPassword ? 'text' : 'password'} placeholder="••••••••"
                {...register('password', { required: 'Password required', minLength: { value: 6, message: 'Min 6 chars' } })}
                className="w-full px-4 py-2 border rounded-lg pr-12 focus:ring-2 focus:ring-purple-600" />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-9 text-gray-600">
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
              {errors.password && <p className="text-red-600 text-sm">{errors.password.message}</p>}
            </div>

            <button type="submit" disabled={loading}
              className="bf-glossy-btn w-full py-3 text-white font-bold rounded-lg disabled:opacity-70 transition">
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-600">
            Admin access? <Link to="/admin/login" className="text-purple-600 hover:underline font-medium">Go to Admin Login</Link>
          </p>
        </div>
      </main>

      <footer className="p-4 text-center bg-black bg-opacity-70 text-gray-300 text-sm">
        © {new Date().getFullYear()} Bolsa Fish
      </footer>
    </div>
  );
}
